import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button/Button';
import { useEvaluationStore, useCurrentStep, useError } from '../../store/evaluationStore';
import { AutoSaveIndicator } from '../../components/AutoSave/AutoSaveIndicator';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner/LoadingSpinner';
import { useToast } from '../../contexts/ToastContext';

// Components
import { ProductRegistration } from '../../components/Steps/ProductRegistration';
import { CriteriaExtraction } from '../../components/Steps/CriteriaExtraction';
import { ComparisonEvaluation } from '../../components/Steps/ComparisonEvaluation';
import { ComparativeEvaluationQuick } from '../../components/Steps/ComparativeEvaluationQuick';
import { GradeAssignment } from '../../components/Steps/GradeAssignment';
import { RubricGeneration } from '../../components/Steps/RubricGeneration';
import { ReportGeneration } from '../../components/Steps/ReportGeneration';

export const EvaluationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { userId, projectId } = useParams<{ userId?: string; projectId?: string }>();
  const [saveLoading, setSaveLoading] = useState(false);
  const [isLoadingExamination, setIsLoadingExamination] = useState(false);
  const { addToast, clearAllToasts } = useToast();
  const previousStepRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // 埋め込みモードかどうかを判定
  const isEmbedded = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('embedded') === 'true' || 
           params.get('token') !== null ||
           window.location.pathname.startsWith('/embed/');
  };
  
  const currentStep = useCurrentStep();
  const error = useError();
  const { 
    resetAll, 
    products = [], 
    criteria = [],
    saveDataAsync, 
    loadEvaluationById, 
    currentExaminationId,
    getEvaluationPosition,
    getGrades,
    isLoading,
    rubricData
  } = useEvaluationStore();
  const set = useEvaluationStore.setState;

  // URLパラメータからプロジェクトを読み込む（安全版）
  useEffect(() => {
    const loadProjectFromUrl = async () => {
      // projectIdが "new" の場合は新規作成モード
      if (projectId === 'new') {
        // 新規作成の場合は何もしない（既にresetAllされている）
        console.log('新規プロジェクト作成モード');
        return;
      }
      
      // projectIdが存在し、かつ現在のIDと異なる場合のみ読み込む
      if (projectId) {
        // 現在のExaminationIdと異なる場合は必ず読み込む
        if (currentExaminationId !== projectId) {
          setIsLoadingExamination(true);
          
          // 現在のIDをクリアして新しいプロジェクトを確実に読み込む
          set({ currentExaminationId: null });
          
          try {
            console.log('プロジェクト読み込み開始:', projectId, '(現在のID:', currentExaminationId, ')');
            const success = await loadEvaluationById(projectId);
            
            if (!success) {
              // 読み込み失敗時は既存データを保持し、エラーメッセージのみ表示
              set({ error: t('evaluation.loadError') + t('evaluation.currentDataRetained') });
              console.warn('プロジェクト読み込み失敗、既存データを保持します');
            } else {
              console.log('プロジェクト読み込み成功');
              set({ error: null }); // 成功時はエラーをクリア
            }
          } catch (error) {
            console.error('プロジェクト読み込みでエラー:', error);
            // エラー時も既存データを保持
            set({ error: `${t('evaluation.loadError')}: ${error instanceof Error ? error.message : t('evaluation.unknownError')} ${t('evaluation.currentDataRetained')}` });
          } finally {
            setIsLoadingExamination(false);
          }
        }
      }
    };

    loadProjectFromUrl();
  }, [projectId]); // currentExaminationIdを依存配列から削除

  // 新規プロジェクトでIDが作成されたらURLを更新
  useEffect(() => {
    if (projectId === 'new' && currentExaminationId) {
      console.log('新規プロジェクトID確定、URLを更新:', currentExaminationId);
      navigate(`/evaluation/${userId || 'unknown'}/${currentExaminationId}`, { replace: true });
    }
  }, [currentExaminationId, projectId, userId, navigate]);

  // 明示的保存機能（改善版）
  const handleSaveData = async () => {
    const { products, criteria, currentExaminationId: currentId } = useEvaluationStore.getState();
    
    if (products.length === 0 && criteria.length === 0) {
      addToast(t('evaluation.noDataToSave'), 'info');
      return;
    }

    setSaveLoading(true);
    
    try {
      console.log('手動保存開始', { currentId, projectId });
      const success = await saveDataAsync();
      
      if (success) {
        addToast(t('evaluation.dataSaved'), 'success');
        console.log('手動保存成功');
        
        // 保存成功後、新しく作成されたプロジェクトIDがある場合はURLを更新
        const newState = useEvaluationStore.getState();
        if (newState.currentExaminationId && (projectId === 'new' || !currentId)) {
          console.log('新規プロジェクトID確定、URL更新:', newState.currentExaminationId);
          navigate(`/evaluation/${userId || 'unknown'}/${newState.currentExaminationId}`, { replace: true });
        }
      } else {
        addToast(t('evaluation.saveFailed') + t('evaluation.dataRetained'), 'error');
        console.warn('手動保存失敗、データは保持されています');
      }
    } catch (error) {
      console.error('手動保存エラー:', error);
      addToast(t('evaluation.saveError') + t('evaluation.dataRetained'), 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/');
    resetAll();
  };

  // エラーをトーストで表示
  useEffect(() => {
    if (error) {
      addToast(t('evaluation.errorOccurred') + error, 'error', 0); // duration 0 = 手動で閉じる必要がある
      set({ error: null });
    }
  }, [error, addToast, set]);

  // 言語変更時にトーストをクリア
  useEffect(() => {
    const handleLanguageChange = () => {
      clearAllToasts();
      // 現在のステップをリセットして、ガイダンスを再表示
      previousStepRef.current = null;
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, clearAllToasts]);

  // ステップが変わった時にガイダンスをトーストで表示
  useEffect(() => {
    console.log('ガイダンスuseEffect実行', { currentStep, isLoading, previousStep: previousStepRef.current });
    
    if (isLoading) {
      console.log('ガイダンストースト: ローディング中のためスキップ');
      return;
    }
    
    // ステップが変わっていない場合はスキップ
    if (previousStepRef.current === currentStep) {
      console.log('ガイダンストースト: ステップ変更なしのためスキップ');
      return;
    }
    
    console.log('ガイダンストースト: ステップ変更検知', { 
      previousStep: previousStepRef.current, 
      currentStep 
    });
    
    // ステップ変更時に全てのトーストを即座にクリア
    clearAllToasts();
    
    // 現在のステップを記録
    previousStepRef.current = currentStep;

    // 3秒後にガイダンスを表示
    const timer = setTimeout(() => {
      console.log('ガイダンストースト: タイマー実行開始');
      // 最新の状態を取得
      const state = useEvaluationStore.getState();
      const currentProducts = state.products || [];
      const currentCriteria = state.criteria || [];
      const currentRubricData = state.rubricData;
      
      // 現在のステップに基づいてガイダンスを設定
      let nextAction = '';
      
      if (currentStep === 1) {
        if (currentProducts.length === 0) {
          nextAction = t('evaluation.guidance.registerProducts');
        } else {
          nextAction = t('evaluation.guidance.completeRegistration');
        }
      } else if (currentStep === 2) {
        if (currentCriteria.length === 0) {
          nextAction = t('evaluation.guidance.extractCriteria');
        } else {
          nextAction = t('evaluation.guidance.confirmCriteria');
        }
      } else if (currentStep === 3) {
        // 必要な比較数（全体の10%または5個の多い方）
        const requiredComparisons = Math.max(5, Math.ceil(currentProducts.length * 0.1));
        
        // 各観点で十分な配置があるかチェック
        const insufficientCriteria = currentCriteria.filter((criterion, criterionIndex) => {
          const placedCount = currentProducts.filter((_, productIndex) => {
            const position = state.getEvaluationPosition(productIndex, criterionIndex);
            return position && position.x !== -1;
          }).length;
          return placedCount < requiredComparisons;
        });
        
        if (insufficientCriteria.length === currentCriteria.length) {
          // 全ての観点が不十分
          nextAction = t('evaluation.guidance.placeProducts', { count: requiredComparisons });
        } else if (insufficientCriteria.length > 0) {
          // 一部の観点が不十分
          const firstInsufficient = insufficientCriteria[0];
          const placedCount = currentProducts.filter((_, productIndex) => {
            const position = state.getEvaluationPosition(productIndex, currentCriteria.indexOf(firstInsufficient));
            return position && position.x !== -1;
          }).length;
          nextAction = t('evaluation.guidance.placeMoreProducts', { 
            criterionTitle: firstInsufficient.title, 
            remaining: requiredComparisons - placedCount 
          });
        } else {
          // 全ての観点で十分な配置がある
          nextAction = t('evaluation.guidance.comparisonComplete');
        }
      } else if (currentStep === 4) {
        const incompleteCriteria = currentCriteria.filter((_, index) => {
          const criterionGrades = state.getGrades(index);
          return !criterionGrades || criterionGrades.length === 0;
        });
        
        if (incompleteCriteria.length > 0) {
          nextAction = t('evaluation.guidance.setGrades', { criterionTitle: incompleteCriteria[0].title });
        } else {
          nextAction = t('evaluation.guidance.gradesComplete');
        }
      } else if (currentStep === 5) {
        if (currentRubricData && currentRubricData.length > 0) {
          nextAction = t('evaluation.guidance.rubricGenerated');
        } else {
          nextAction = t('evaluation.guidance.generateRubric');
        }
      } else if (currentStep === 6) {
        // 全体の評価数を計算
        const totalEvaluations = currentProducts.length * currentCriteria.length;
        const completedEvaluations = currentCriteria.reduce((total, _, criterionIndex) => {
          return total + currentProducts.filter((_, productIndex) => {
            const position = state.getEvaluationPosition(productIndex, criterionIndex);
            return position && position.x !== -1;
          }).length;
        }, 0);
        const unplacedCount = totalEvaluations - completedEvaluations;
        const progress = Math.floor((completedEvaluations / totalEvaluations) * 100);
        
        if (unplacedCount > 0) {
          // 進捗に応じた励ましメッセージ
          if (progress < 25) {
            nextAction = t('evaluation.guidance.startEvaluation');
          } else if (progress < 50) {
            nextAction = t('evaluation.guidance.progress25', { progress });
          } else if (progress < 75) {
            nextAction = t('evaluation.guidance.progress50', { progress });
          } else if (progress < 90) {
            nextAction = t('evaluation.guidance.progress75');
          } else {
            nextAction = t('evaluation.guidance.almostDone', { unplacedCount });
          }
        } else {
          nextAction = t('evaluation.guidance.allComplete');
        }
      } else if (currentStep === 7) {
        nextAction = t('evaluation.guidance.downloadReport');
      }

      console.log('ガイダンストースト: メッセージ生成', { currentStep, nextAction });
      if (nextAction) {
        console.log('ガイダンストースト: トースト表示実行');
        addToast(nextAction, 'info', 10000); // 10秒表示
      }
    }, 3000); // 3秒後に実行

    // クリーンアップ
    return () => clearTimeout(timer);
  }, [currentStep, isLoading, addToast, clearAllToasts, t]);
  
  // 進捗状況の監視（7秒間変化がない場合にガイダンスを表示）
  useEffect(() => {
    // 進捗を検出する関数
    const checkProgress = () => {
      const state = useEvaluationStore.getState();
      const currentProducts = state.products || [];
      const currentCriteria = state.criteria || [];
      const currentRubricData = state.rubricData;
      
      let progressGuidance = '';
      
      if (currentStep === 3) {
        // 必要な比較数の計算
        const requiredComparisons = Math.max(5, Math.ceil(currentProducts.length * 0.1));
        
        // 最も進捗が遅れている観点を見つける
        let leastProgressCriterion = null;
        let leastProgressCount = currentProducts.length;
        
        currentCriteria.forEach((criterion, criterionIndex) => {
          const placedCount = currentProducts.filter((_, productIndex) => {
            const position = state.getEvaluationPosition(productIndex, criterionIndex);
            return position && position.x !== -1;
          }).length;
          
          if (placedCount < requiredComparisons && placedCount < leastProgressCount) {
            leastProgressCriterion = criterion;
            leastProgressCount = placedCount;
          }
        });
        
        if (leastProgressCriterion) {
          progressGuidance = t('evaluation.reminder.continuePlacing', {
            criterionTitle: (leastProgressCriterion as any).title,
            remaining: requiredComparisons - leastProgressCount
          });
        }
      } else if (currentStep === 6) {
        // 現在評価中の観点を特定
        let currentCriterionIndex = -1;
        let minUnplacedInCriterion = currentProducts.length;
        
        currentCriteria.forEach((criterion, criterionIndex) => {
          const unplacedInCriterion = currentProducts.filter((_, productIndex) => {
            const position = state.getEvaluationPosition(productIndex, criterionIndex);
            return !position || position.x === -1;
          }).length;
          
          // 未配置が残っていて、最も進捗が進んでいる観点を選択
          if (unplacedInCriterion > 0 && unplacedInCriterion < minUnplacedInCriterion) {
            currentCriterionIndex = criterionIndex;
            minUnplacedInCriterion = unplacedInCriterion;
          }
        });
        
        if (currentCriterionIndex >= 0) {
          const criterion = currentCriteria[currentCriterionIndex];
          const placedInCriterion = currentProducts.length - minUnplacedInCriterion;
          const progressInCriterion = Math.floor((placedInCriterion / currentProducts.length) * 100);
          
          if (progressInCriterion < 50) {
            progressGuidance = t('evaluation.reminder.almostHalf', { criterionTitle: criterion.title });
          } else if (progressInCriterion < 75) {
            progressGuidance = t('evaluation.reminder.overHalf', { criterionTitle: criterion.title });
          } else if (progressInCriterion < 100) {
            progressGuidance = t('evaluation.reminder.fewLeft', { 
              criterionTitle: criterion.title,
              remaining: minUnplacedInCriterion
            });
          }
        }
      }
      
      if (progressGuidance) {
        console.log('進捗リマインダー:', progressGuidance);
        addToast(progressGuidance, 'info', 8000);
      }
    };
    
    // タイマーのセットアップ
    const setupInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      // ステップ3（比較評価）またはステップ6（クイック評価）の場合のみ監視
      if (currentStep === 3 || currentStep === 6) {
        inactivityTimerRef.current = setTimeout(() => {
          checkProgress();
          // 再度タイマーをセット（連続的な監視）
          setupInactivityTimer();
        }, 7000); // 7秒後
      }
    };
    
    // アクティビティの記録とタイマーリセット
    const resetActivityTimer = () => {
      lastActivityRef.current = Date.now();
      setupInactivityTimer();
    };
    
    // 進捗変更を検出するための監視
    const unsubscribe = useEvaluationStore.subscribe(resetActivityTimer);
    
    // 初回セットアップ
    setupInactivityTimer();
    
    // クリーンアップ
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      unsubscribe();
    };
  }, [currentStep, addToast, t]);

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <ProductRegistration />;
      case 2:
        return <CriteriaExtraction />;
      case 3:
        return <ComparisonEvaluation />;
      case 4:
        return <GradeAssignment />;
      case 5:
        return <RubricGeneration />;
      case 6:
        return <ComparativeEvaluationQuick />;
      case 7:
        return <ReportGeneration />;
      default:
        return <ProductRegistration />;
    }
  };

  // ローディング中の表示（プロジェクトIDがあり、newでない場合は読み込み完了まで待つ）
  if (isLoadingExamination || (projectId && projectId !== 'new' && !currentExaminationId)) {
    return (
      <div className="evaluation-workspace">
        <div className="app-main__container">
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            minHeight: '100vh',
            justifyContent: 'flex-start',
            paddingTop: '30vh'
          }}>
            <LoadingSpinner size="lg" />
            <p style={{ marginTop: '0.5rem' }}>{t('evaluation.loadingProject')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="evaluation-workspace">
      <AutoSaveIndicator className="evaluation-auto-save" />
      <div className="app-main__container">
        {renderCurrentStep()}
      </div>
      {!isEmbedded() && (
        <div className="app-actions">
          <Button
            variant="secondary"
            onClick={handleSaveData}
            className="save-button"
            disabled={saveLoading}
          >
            {saveLoading ? t('evaluation.saving') : t('common.save')}
          </Button>
          <Button
            variant="primary"
            onClick={handleBackToDashboard}
            className="back-to-dashboard"
          >
            {t('common.backToDashboard')}
          </Button>
        </div>
      )}
    </div>
  );
};